import React from 'react';
import SampleFileDownloader from './SampleFileDownloader.jsx';
import { AlertCircle, X, Download } from 'lucide-react'; 
import * as XLSX from 'xlsx'; // Import XLSX for error downloading
const UploadForm = ({ onFileChange,onUpload,collectors,uploadCollector,setUploadCollector,isUploading,failedRecords,onClearErrors }) => {

    const handleDownloadErrors = () => {
        if (!failedRecords || failedRecords.length === 0) return;

        // 1. Define Headers Same As Sample File 
        const headers = [
            'provider','renderingFacility','taxId','ptName', 'dob', 'ssn', 'employer', 'insurance', 
            'bill', 'paid', 'outstanding', 'ledger', 'hcf', 'invoice', 'signinSheet',
            'fds', 'lds', 'solDate',
            'hearingStatus', 'hearingDate', 'hearingTime', 'judgeName','courtRoomlink','judgePhone','AccesCode','boardLocation',
            'lienStatus', 'caseStatus', 'caseDate', 'crAmount', 'dorFiledBy', 'status4903_8', 'pmrStatus', 'judgeOrderStatus', 'adjuster', 'adjusterPhone',
            'adjusterFax', 'adjusterEmail', 'defenseAttorney', 'defenseAttorneyPhone',
            'defenseAttorneyFax', 'defenseAttorneyEmail',
            'claimNo.1', 'claimNo.2',
            'adjNumber.1', 'adjNumber.2',
            'doi.1', 'doi.2',
            'Error Reason' // Added column to show why it failed
        ];

        // 2. Map the data back to the flat structure
        const dataRows = failedRecords.map(rec => {
            // Helper to get array values safely
            const getClaimVal = (idx) => rec.claimNo && rec.claimNo[idx] ? rec.claimNo[idx].value : '';
            const getAdjVal = (idx) => rec.adjNumber && rec.adjNumber[idx] ? rec.adjNumber[idx].value : '';
            const getDoiVal = (idx) => rec.doi && rec.doi[idx] ? rec.doi[idx].value : '';

            return [
                rec.provider, rec.renderingFacility, rec.taxId, rec.ptName, rec.dob, rec.ssn, rec.employer, rec.insurance,
                rec.bill, rec.paid, rec.outstanding, rec.ledger, rec.hcf, rec.invoice, rec.signinSheet,
                rec.fds, rec.lds, rec.solDate,
                rec.hearingStatus, rec.hearingDate, rec.hearingTime, rec.judgeName, rec.courtRoomlink, rec.judgePhone, rec.AccesCode, rec.boardLocation,
                rec.lienStatus, rec.caseStatus, rec.caseDate, rec.crAmount, rec.dorFiledBy, rec.status4903_8, rec.pmrStatus, rec.judgeOrderStatus,
                rec.adjuster, rec.adjusterPhone, rec.adjusterFax, rec.adjusterEmail,
                rec.defenseAttorney, rec.defenseAttorneyPhone, rec.defenseAttorneyFax, rec.defenseAttorneyEmail,
                getClaimVal(0), getClaimVal(1), // claimNo.1, claimNo.2
                getAdjVal(0), getAdjVal(1),     // adjNumber.1, adjNumber.2
                getDoiVal(0), getDoiVal(1),     // doi.1, doi.2
                rec.errorReason // The error message
            ];
        });

        // 3. Generate Worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);

        // 4. Force Text Format to prevent Excel from messing up dates/numbers
        const textFormat = '@';
        const range = XLSX.utils.decode_range(ws['!ref']);
        
        // Loop through all cells and force text type (s) and text format (@)
        for(let R = range.s.r; R <= range.e.r; ++R) {
            for(let C = range.s.c; C <= range.e.c; ++C) {
                const cell_address = {c:C, r:R};
                const cell_ref = XLSX.utils.encode_cell(cell_address);
                if(!ws[cell_ref]) continue;
                
                // Keep header row as is, format data rows
                if(R > 0) {
                     ws[cell_ref].t = 's'; // type: string
                     ws[cell_ref].z = textFormat; // format: text
                }
            }
        }

        XLSX.utils.book_append_sheet(wb, ws, "Failed Records");
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        // 5. Trigger Download
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'failed_records.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="bg-white rounded-lg  p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Upload Records</h2>
            <form onSubmit={onUpload} id="file-upload-form" className="space-y-4">

                <div>
                    <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700">
                        CSV/XLSX File
                    </label>
                    <input
                        id="file-upload"
                        type="file"
                        onChange={onFileChange}
                        accept=".xlsx, .csv"
                        disabled={isUploading}
                        className="mt-1 block w-full text-sm text-gray-500 
                        file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 
                        file:font-semibold file:bg-orange-50 file:text-orange-700 
                        hover:file:bg-orange-100 disabled:opacity-50"
                    />
                    <SampleFileDownloader />
                </div>

                <div>
                    <label htmlFor="collector-select" className="block text-sm font-medium text-gray-700">
                        Assign to Collector (Optional)
                    </label>
                    <select
                        id="collector-select"
                        value={uploadCollector}
                        onChange={(e) => setUploadCollector(e.target.value)}
                        disabled={isUploading}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 
                        focus:outline-none focus:ring-orange-500 focus:border-orange-500 
                        sm:text-sm rounded-md disabled:bg-gray-200"
                    >
                        <option value="">Do not assign</option>
                        {collectors.map(c => (
                            <option key={c._id} value={c._id}>{c.username}</option>
                        ))}
                    </select>
                </div>

                <div className="flex justify-center">
                    <button
                        type="submit"
                        disabled={isUploading}
                        className="flex justify-center items-center bg-green-800 hover:bg-green-900 
                        text-white font-semibold rounded-lg shadow-md 
                        transition-all duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed
                        w-60 h-12"
                    >
                        {isUploading ? (
                            <>
                                <svg
                                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 
                                        5.373 0 12h4zm2 5.291A7.962 7.962 
                                        0 014 12H0c0 3.042 1.135 5.824 3 
                                        7.938l3-2.647z"
                                    ></path>
                                </svg>
                                Uploading...
                            </>
                        ) : (
                            'Upload File'
                        )}
                    </button>
                </div>
            </form>

            {/* --- ERROR POPUP / MODAL --- */}
            {failedRecords && failedRecords.length > 0 && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        {/* Background overlay */}
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClearErrors}></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                            <div className="bg-red-50 px-4 py-3 sm:px-6 flex justify-between items-center border-b border-red-100">
                                <div className="flex items-center">
                                    <AlertCircle className="h-6 w-6 text-red-600 mr-2" />
                                    <h3 className="text-lg leading-6 font-medium text-red-800" id="modal-title">
                                        Upload Errors ({failedRecords.length} records failed)
                                    </h3>
                                </div>
                                <button
                                    onClick={onClearErrors}
                                    className="bg-red-100 rounded-full p-1 hover:bg-red-200 focus:outline-none transition-colors"
                                >
                                    <X className="h-5 w-5 text-red-700" />
                                </button>
                            </div>
                            
                            <div className="px-4 py-4 sm:p-6 max-h-[60vh] overflow-y-auto">
                                <p className="text-sm text-gray-600 mb-4">
                                    The following records could not be uploaded because they contain data that does not match the backend requirements.
                                </p>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Pt Name
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Provider
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Error Reason
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {failedRecords.map((record, index) => (
                                                <tr key={index} className="hover:bg-red-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {record.ptName || 'N/A'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {record.provider || 'N/A'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-red-600">
                                                        {record.errorReason}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    onClick={onClearErrors}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    Close & Retry
                                </button>

                                {/* Added Download Button */}
                                <button
                                    type="button"
                                    onClick={handleDownloadErrors}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm items-center"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download Errors (.xlsx)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UploadForm;